import { useEffect, useState } from 'react';
import useAuthStore from '../store/authStore';
import { authService } from '../api/services';

/**
 * Hook to initialize authentication state on application startup
 * @returns {Object} Authentication state and initialization status
 */
export const useAuthInit = () => {
  const [initialized, setInitialized] = useState(false);
  const { user, isAuthenticated, refreshToken, loadUser, startTokenRefresh } = useAuthStore();

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem('jms_token');

        if (!token) {
          if (mounted) setInitialized(true);
          return;
        }

        // If we already have a valid user in the store, we're done
        if (isAuthenticated && user) {
          // Start token refresh timer
          startTokenRefresh();
          if (mounted) setInitialized(true);
          return;
        }

        // Try to refresh the token if needed
        const refreshed = await refreshToken();

        // If refresh succeeded or we have a valid token, try to load the user profile
        if (refreshed || isAuthenticated) {
          try {
            await loadUser();
            // Start token refresh timer
            startTokenRefresh();
          } catch (error) {
            console.error('Failed to load user profile:', error);
            // Clear invalid tokens
            localStorage.removeItem('jms_token');
            localStorage.removeItem('jms_refresh_token');
          }
        }
      } catch (error) {
        console.error('Failed to initialize authentication:', error);
        // Clear invalid tokens
        localStorage.removeItem('jms_token');
        localStorage.removeItem('jms_refresh_token');
      } finally {
        if (mounted) setInitialized(true);
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
      // Clean up token refresh timer when component unmounts
      useAuthStore.getState().stopTokenRefresh();
    };
  }, []); // Remove dependencies to prevent re-runs

  return { initialized, isAuthenticated, user };
};
